export const DEFAULT_PUBLIC_ORIGIN = "https://yes7tickets.net";

/** Deployment configuration only: never derive canonical URLs from request headers. */
export function trustedOrigin(value: string): string | null {
  try {
    const url = new URL(value);
    const loopback = ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname);
    if (
      url.username ||
      url.password ||
      url.search ||
      url.hash ||
      url.pathname !== "/" ||
      (url.protocol !== "https:" && !(loopback && url.protocol === "http:"))
    )
      return null;
    return url.origin;
  } catch {
    return null;
  }
}

export function isPublicEventPath(path: string): boolean {
  return /^\/event\/[1-9]\d*$/.test(path);
}

export function safeImageUrl(value: string, origin: string): string | null {
  if (!value || !/^https:\/\//i.test(value)) return null;
  try {
    const url = new URL(value, origin);
    if (url.protocol !== "https:" || url.username || url.password) return null;
    return url.href;
  } catch {
    return null;
  }
}

export function explicitDate(value: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(
      value,
    ) && Number.isFinite(Date.parse(value))
  );
}

const PUBLIC_STATIC_PATHS = [
  "/",
  "/about-us",
  "/privacy-policy",
  "/terms-of-use",
  "/half-price",
  "/frequently-asked-questions",
  "/talk-to-us",
];
export function publicStaticPath(path: string): boolean {
  return PUBLIC_STATIC_PATHS.includes(path);
}

const xmlEscape = (value: string) =>
  value.replace(
    /[<>&"']/g,
    (char) =>
      ({
        "<": "&lt;",
        ">": "&gt;",
        "&": "&amp;",
        '"': "&quot;",
        "'": "&apos;",
      })[char]!,
  );
export function renderEventSitemap(
  origin: string,
  events: { id: number; updated_at: string }[],
): string {
  if (!trustedOrigin(origin) || events.length > 500)
    throw new Error("Invalid sitemap input");
  const entries = events
    .filter((event) => Number.isSafeInteger(event.id) && event.id > 0)
    .map(
      (event) =>
        `<url><loc>${xmlEscape(origin)}/event/${event.id}</loc>${explicitDate(event.updated_at) && Date.parse(event.updated_at) <= Date.now() ? `<lastmod>${xmlEscape(event.updated_at)}</lastmod>` : ""}</url>`,
    )
    .join("");
  return `<?xml version="1.0" encoding="UTF-8"?><urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">${entries}</urlset>`;
}
export function renderSitemapIndex(origin: string, pages: number): string {
  if (
    !trustedOrigin(origin) ||
    !Number.isInteger(pages) ||
    pages < 1 ||
    pages > 50000
  )
    throw new Error("Invalid sitemap index");
  return `<?xml version="1.0" encoding="UTF-8"?><sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">${Array.from({ length: pages }, (_, index) => `<sitemap><loc>${xmlEscape(origin)}/sitemaps/events-${index + 1}.xml</loc></sitemap>`).join("")}</sitemapindex>`;
}
export function robotsText(origin: string, enabled: boolean): string {
  if (!enabled || !trustedOrigin(origin)) return "User-agent: *\nDisallow: /\n";
  return `User-agent: *\nAllow: /\nDisallow: /producer-area/\nDisallow: /account/\nDisallow: /order-checkout/\nDisallow: /cart\nDisallow: /my-orders\nDisallow: /checkout-success\nSitemap: ${origin}/sitemap.xml\n`;
}
